// ============================================================
// TMR Portal Admin — leituras e escritas da Contingência, sobre @tmr/api-client (31 §9)
// ============================================================
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { api } from '@tmr/api-client';
import { contingencyKeys } from '../queryClient';
import type {
  ContingencyRequest,
  ContingencyRequestPage,
  ContingencyRequestStatus,
  ContingencyType,
  ContingencyTypeKey,
  ContingencyTypeList,
  DecideContingencyRequest,
  RequestWildcardCodeRequest,
  UpdateContingencySettingRequest,
  WildcardCodeResponse,
} from './contract';

/**
 * Every kind of contingency with its setting.
 *
 * @param enabled Whether the operator holds `contingency.access` — false skips a request that would 403.
 */
export function useContingencyTypes(enabled = true): UseQueryResult<ContingencyTypeList> {
  return useQuery({
    queryKey: contingencyKeys.types(),
    queryFn: ({ signal }) => api.get<ContingencyTypeList>('/contingency/types', { signal }),
    enabled,
  });
}

/** The filters and page of the request list. */
export interface ContingencyRequestFilters {
  type?: ContingencyTypeKey;
  status?: ContingencyRequestStatus;
  page?: number;
  pageSize?: number;
}

/**
 * The requests, newest first. Refetched on focus: a manager deciding in another tab is exactly the change the
 * requester is waiting to see (`09` §11, N2).
 *
 * @param filters Kind, status and page.
 * @param enabled Whether the operator holds `contingency.access`.
 */
export function useContingencyRequests(
  filters: ContingencyRequestFilters,
  enabled = true,
): UseQueryResult<ContingencyRequestPage> {
  return useQuery({
    queryKey: contingencyKeys.requests({ ...filters }),
    queryFn: ({ signal }) =>
      api.get<ContingencyRequestPage>('/contingency/requests', {
        signal,
        query: { type: filters.type, status: filters.status, page: filters.page, pageSize: filters.pageSize },
      }),
    enabled,
    refetchOnWindowFocus: true,
  });
}

/** Switches a kind on or off and sets whether it needs approval — the manager's. */
export function useUpdateContingencySetting() {
  return useContingencyMutation(({ key, ...setting }: { key: ContingencyTypeKey } & UpdateContingencySettingRequest) =>
    api.put<ContingencyType>(`/contingency/types/${key}`, setting satisfies UpdateContingencySettingRequest));
}

/**
 * Asks for a wildcard code. Without approval the answer carries the code; with approval, the request waiting.
 * The code lives only in the mutation's data — never in the query cache, which outlives the dialog.
 */
export function useRequestWildcardCode() {
  return useContingencyMutation((body: RequestWildcardCodeRequest) =>
    api.post<WildcardCodeResponse>('/contingency/wildcard-codes', body));
}

/** Generates the code of an approved request — the requester's, once. */
export function useIssueApprovedWildcardCode() {
  return useContingencyMutation((requestId: string) =>
    api.post<WildcardCodeResponse>(`/contingency/wildcard-codes/${requestId}/issue`));
}

/** Approves or rejects a request waiting for approval — the manager's, never their own. */
export function useDecideContingencyRequest() {
  return useContingencyMutation(
    ({ requestId, outcome, note }: { requestId: string; outcome: 'approve' | 'reject'; note: string }) =>
      api.post<ContingencyRequest>(`/contingency/requests/${requestId}/${outcome}`, {
        note: note.trim() || null,
      } satisfies DecideContingencyRequest),
  );
}

/**
 * Shared plumbing for every write: run it, then invalidate the whole panel — a setting moves the cards, and a
 * request moves the list and the pending count on its card.
 *
 * @param mutationFn The request itself.
 */
function useContingencyMutation<TVariables, TResult>(mutationFn: (variables: TVariables) => Promise<TResult>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: contingencyKeys.all });
    },
  });
}
